import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, signal, ViewChild, OnInit, inject } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
  FormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SharedService } from '@services/shared.service';
import { CountryService } from '@services/country.service';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { AuthService } from 'src/app/Auth/AuthService';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import {
  CountryISO,
  SearchCountryField,
  NgxIntlTelInputComponent,
} from 'ngx-intl-tel-input';
import { PagesService } from '@services/pages.service';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '@env';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    NgxIntlTelInputModule,
    CommonModule,
    NzToolTipModule,
    TranslateModule,
    FormsModule,
  ],
  templateUrl: './signup.component.html',
  // styleUrl: './signup.component.scss',
  styleUrls: ['./signup.component.scss', '../../../../auth.scss'],

  host: { ngSkipHydration: 'true' },
})
export class SignupComponent implements OnInit {
  baseUrl = environment.baseUrl;
  SignupForm: FormGroup;
  isPasswordVisible = false;
  isLoading = signal(false);
  pagesService = inject(PagesService);
  private _router = inject(Router);
  selectedCountryData: any;

  CountryISO = CountryISO;
  SearchCountryField = SearchCountryField;

  @ViewChild(NgxIntlTelInputComponent)
  phoneInputComponent!: NgxIntlTelInputComponent;

  agreeTerms: boolean = false;
  planData: any = null;
  selectedCountryISO: CountryISO = CountryISO.Pakistan;

  passwordStrength = {
    lowercase: false,
    uppercase: false,
    number: false,
    specialChar: false,
    minLength: false,
  };

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private http: HttpClient,
    private authService: AuthService,
    private route: ActivatedRoute,
    private sharedService: SharedService,
    private countryService: CountryService
  ) {
    this.SignupForm = this.fb.group({
      fname: ['', [Validators.required, Validators.minLength(2)]],
      lname: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      phone_number: ['', [Validators.required]],
      password: ['', [Validators.required, Validators.minLength(8), Validators.pattern('^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+\\-=[\\]{};\'"\\\\|,.<>/?]).{8,}$')]],
      policy: [false, Validators.requiredTrue],
      country: [''],
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const token = params['token'];
      if (token) {
        try {
          const decoded = atob(token);
          this.planData = JSON.parse(decoded);
        } catch (err) {
          console.error('Invalid token format', err);
          this.planData = null;
        }
      }
    });

    this.detectUserCountry();
  }



  async onSubmit() {
    if (this.SignupForm.invalid) {
      this.SignupForm.markAllAsTouched();
      this.sharedService.createNotification(
        'error',
        'Please fill in all required fields correctly.'
      );
      return;
    }

    this.sharedService.showLoader();

    const selectedCountry = this.phoneInputComponent?.selectedCountry;
    if (selectedCountry?.name) {
      this.SignupForm.get('country')?.setValue(selectedCountry.name);
    }

    try {
      const phoneObj = this.SignupForm.get('phone_number')?.value;

      if (!phoneObj || !phoneObj.e164Number) {
        this.sharedService.createNotification(
          'error',
          'Valid phone number is required'
        );
        this.sharedService.hideLoader();
        return;
      }

      const finalPhone = phoneObj.e164Number;

      const payload = {
        ...this.SignupForm.value,
        phone_number: finalPhone,
        name: `${this.SignupForm.value.fname} ${this.SignupForm.value.lname}`,
      };

      this.isLoading.set(true);
      this.proceedWithRegistration(payload);
    } catch (error) {
      console.error('Error processing phone number:', error);
      this.sharedService.hideLoader();
      this.sharedService.createNotification(
        'error',
        'An error occurred while processing your phone number.'
      );
    }
  }

  private proceedWithRegistration(data: any) {
    this.authService.register('SIGNUP', data).subscribe({
      next: (response: any) => {
        this.isLoading.set(false);
        this.sharedService.hideLoader();

        if (response && response.success !== false) {
          this.authService.setUserToken(response);
          const token = this.createSecretToken(this.planData);
          this.router.navigate(['auth/company'], { queryParams: { token } });
          this.sharedService.createNotification(
            'success',
            'Registration successful!'
          );
        } else {
          this.sharedService.createNotification(
            'error',
            response?.message || 'Signup failed. Please try again.'
          );
        }
      },
      error: (error: any) => {
        this.isLoading.set(false);
        this.sharedService.hideLoader();

        const backendMsg =
          error?.error?.message ||
          error?.error?.result1 ||
          error?.message;

        if (backendMsg) {
          this.sharedService.createNotification('error', backendMsg);
        } else if (error?.status === 403) {
          this.sharedService.createNotification(
            'error',
            'Access denied. Please contact support.'
          );
        } else if (error?.status === 409) {
          this.sharedService.createNotification(
            'error',
            'User already exists with this email or phone number.'
          );
        } else {
          this.sharedService.createNotification(
            'error',
            'Signup failed. Please try again later.'
          );
        }
      },
    });
  }

  createSecretToken(plan: any): string {
    const payload = plan;
    return btoa(JSON.stringify(payload));
  }

  isRequired(controlName: string): boolean {
    const control = this.SignupForm.get(controlName);
    return control ? control.hasValidator(Validators.required) : false;
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  private getMissingPasswordRequirements(): string[] {
    const missing: string[] = [];
    if (!this.passwordStrength.lowercase) missing.push('lowercase letter');
    if (!this.passwordStrength.uppercase) missing.push('uppercase letter');
    if (!this.passwordStrength.number) missing.push('number');
    if (!this.passwordStrength.specialChar) missing.push('special character');
    if (!this.passwordStrength.minLength)
      missing.push('minimum length of 8 characters');
    return missing;
  }

  goToPage(routeLink: string) {
    const tree = this._router.createUrlTree([routeLink]);
    const url = this._router.serializeUrl(tree);
    const fullUrl = `${window.location.origin}#${url}`;
    window.open(fullUrl, '_blank');
  }

  private detectUserCountry() {
    this.http
      .get<any>(
        'https://api.ipgeolocation.io/ipgeo?apiKey=7394c0666cc84f05b9801f43d229666b'
      )
      .subscribe({
        next: (data) => {
          if (data && data.country_code2) {
            this.setCountryFromCode(data.country_code2);
          }
        },
        error: (err) => {
          console.warn('Could not detect country:', err);
        },
      });
  }

  private setCountryFromCode(countryCode: string) {
    const code = countryCode.toUpperCase();
    const countryKey = Object.keys(CountryISO).find(
      (key) => CountryISO[key as keyof typeof CountryISO] === code.toLowerCase()
    );

    if (countryKey) {
      this.selectedCountryISO =
        CountryISO[countryKey as keyof typeof CountryISO];

      setTimeout(() => {
        if (this.phoneInputComponent) {
          this.phoneInputComponent.selectedCountryISO = this.selectedCountryISO;
        }
      }, 300);
    }
  }
}